import * as React from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  Grid,
  TextField,
  Switch,
  FormControlLabel,
  FormLabel,
  MenuItem,
  Select,
  IconButton,
  Divider,
  Stack,
  Box,
  Chip,
  InputAdornment
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import PersonIcon from '@mui/icons-material/Person';
import HomeIcon from '@mui/icons-material/Home';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import DiscountIcon from '@mui/icons-material/Discount';
import PaymentIcon from '@mui/icons-material/Payment';
import PercentIcon from '@mui/icons-material/Percent';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { useState, useEffect } from 'react';
import { postApi, getApi } from 'views/services/api';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router';
import PropTypes from 'prop-types';

const RoomInvoiceDialog = (props) => {
  const navigate = useNavigate();
  const { open, handleClose, data } = props;
  const [hotelData, setHotelData] = useState([]);
  const [haveGST, setHaveGST] = useState(true);
  const [gstAmount, setGstAmount] = useState();
  const hotel = JSON.parse(localStorage.getItem('hotelData'));
  const currency = hotel?.currency || 'USD';

  // Función para obtener datos del hotel
  const fetchHotelData = async () => {
    try {
      const response = await getApi(`api/hotel/view/${hotel?.hotelId}`);
      setHotelData(response?.data);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    fetchHotelData();
  }, []);

  const initialValues = {
    name: `${data?.customerDetails?.firstName} ${data?.customerDetails?.lastName}`,
    address: data?.customerDetails?.address,
    roomRent: data?.totalPayment,
    advanceAmount: data?.advanceAmount,
    pendingAmount: data?.totalAmount,
    discount: '',
    gstPercentage: hotelData?.roomgstpercentage,
    haveGST: true,
    gstNumber: data?.customerDetails?.idcardNumber || '',
    totalAmount: '',
    paymentMethod: 'cash'
  };

  const validationSchema = yup.object().shape({
    name: yup.string().required('El nombre del cliente es requerido'),
    address: yup.string().required('La dirección es requerida'),
    roomRent: yup.number().required('El precio de la habitación es requerido').min(0, 'El precio no puede ser negativo'),
    pendingAmount: yup.number().required('El monto pendiente es requerido').min(0, 'El monto pendiente no puede ser negativo'),
    advanceAmount: yup.number().required('El adelanto es requerido').min(0, 'El adelanto no puede ser negativo'),
    discount: yup.number().min(0, 'El descuento no puede ser negativo'),
    gstPercentage: yup.number().min(0, 'El porcentaje de impuesto no puede ser negativo'),
    gstNumber: yup.string().when('haveGST', {
      is: true,
      then: yup.string(),
      otherwise: yup.string()
    }),
    totalAmount: yup.number().required('El monto total es requerido').min(0, 'El monto total no puede ser negativo'),
    paymentMethod: yup.string().required('El método de pago es requerido')
  });

  // Función para generar número de factura
  const generateInvoiceNumber = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');

    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let randomPart = '';
    const charactersLength = characters.length;
    for (let i = 0; i < 4; i++) {
      randomPart += characters.charAt(Math.floor(Math.random() * charactersLength));
    }
    const invoiceNumber = `INV-${year}-${month}-${day}-${randomPart}`;
    return invoiceNumber;
  };

  // Función para generar factura
  const generateRoomInvoice = async (values, resetForm) => {
    try {
      values.type = 'room';
      values.hotelId = hotel?.hotelId;
      values.reservationId = data?._id;
      values.invoiceNumber = generateInvoiceNumber();
      values.customerPhoneNumber = data?.customerDetails?.phoneNumber;
      values.gstAmount = gstAmount;
      const response = await postApi('api/invoice/add', values);
      if (!hotelData.roomgstpercentage) {
        const data = {};
        data.roomgstpercentage = formik.values.gstPercentage;
      }

      toast.success('Factura generada exitosamente');
      handleClose();
      resetForm();
      setTimeout(() => {
        navigate(`/roombill/view/${response.data._id}`);
      }, 200);
    } catch (error) {
      toast.error('No se puede generar la factura');
    }
  };

  const formik = useFormik({
    initialValues,
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values, { resetForm }) => {
      generateRoomInvoice(values, resetForm);
    }
  });

  const handleGSTToggle = () => {
    setHaveGST(!haveGST);
    formik.setFieldValue('haveGST', !haveGST);
  };

  const calculateTotalAmount = () => {
    // Total a pagar por el cliente (Precio final)
    const totalAmount = formik.values.roomRent - formik.values.discount;
    let gstAmount = 0;

    if (haveGST && formik.values.gstPercentage > 0) {
      // Cálculo Impuesto Incluido (Tax-Inclusive): Total = Base + (Base * %GST)
      // Por ende: Base = Total / (1 + %GST)
      const gstRate = formik.values.gstPercentage / 100;
      const baseAmount = totalAmount / (1 + gstRate);
      gstAmount = totalAmount - baseAmount;
    }

    setGstAmount(gstAmount);
    formik.setFieldValue('totalAmount', totalAmount);
  };

  useEffect(() => {
    calculateTotalAmount();
  }, [formik.values.pendingAmount, formik.values.discount, formik.values.gstPercentage, haveGST, formik.values.advanceAmount]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
        }
      }}
    >
      {/* Header */}
      <DialogTitle sx={{ pb: 2 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                bgcolor: 'primary.main',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <ReceiptLongIcon sx={{ color: 'white', fontSize: 24 }} />
            </Box>
            <Box>
              <Typography variant="h5" fontWeight={600}>
                Información de Factura
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Generar factura de habitación
              </Typography>
            </Box>
          </Stack>
          <IconButton
            onClick={handleClose}
            sx={{
              color: 'text.secondary',
              '&:hover': {
                bgcolor: 'error.lighter',
                color: 'error.main'
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 3, pb: 2 }}>
        <form onSubmit={formik.handleSubmit}>
          <Grid container spacing={3}>
            {/* Información del Cliente */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
                Información del Cliente
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormLabel sx={{ mb: 1, display: 'block', fontWeight: 500 }}>Nombre del Cliente (Facturar a)</FormLabel>
              <TextField
                id="name"
                name="name"
                size="small"
                fullWidth
                placeholder="Ingrese el nombre del cliente"
                value={formik.values.name}
                onChange={formik.handleChange}
                error={formik.touched.name && Boolean(formik.errors.name)}
                helperText={formik.touched.name && formik.errors.name}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon sx={{ color: 'action.active', fontSize: 20 }} />
                    </InputAdornment>
                  )
                }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormLabel sx={{ mb: 1, display: 'block', fontWeight: 500 }}>Dirección</FormLabel>
              <TextField
                id="address"
                name="address"
                size="small"
                fullWidth
                placeholder="Ingrese la dirección"
                value={formik.values.address}
                onChange={formik.handleChange}
                error={formik.touched.address && Boolean(formik.errors.address)}
                helperText={formik.touched.address && formik.errors.address}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <HomeIcon sx={{ color: 'action.active', fontSize: 20 }} />
                    </InputAdornment>
                  )
                }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>

            {/* Detalles de Pago */}
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2, mt: 2 }}>
                Detalles de Pago
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormLabel sx={{ mb: 1, display: 'block', fontWeight: 500 }}>Precio de Habitación</FormLabel>
              <TextField
                id="roomRent"
                name="roomRent"
                size="small"
                type="number"
                fullWidth
                value={formik.values.roomRent}
                onChange={formik.handleChange}
                error={formik.touched.roomRent && Boolean(formik.errors.roomRent)}
                helperText={formik.touched.roomRent && formik.errors.roomRent}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <AttachMoneyIcon sx={{ color: 'action.active', fontSize: 20 }} />
                    </InputAdornment>
                  )
                }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormLabel sx={{ mb: 1, display: 'block', fontWeight: 500 }}>Monto de Adelanto</FormLabel>
              <TextField
                id="advanceAmount"
                name="advanceAmount"
                size="small"
                type="number"
                fullWidth
                value={formik.values.advanceAmount}
                onChange={formik.handleChange}
                error={formik.touched.advanceAmount && Boolean(formik.errors.advanceAmount)}
                helperText={formik.touched.advanceAmount && formik.errors.advanceAmount}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <AttachMoneyIcon sx={{ color: 'action.active', fontSize: 20 }} />
                    </InputAdornment>
                  )
                }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormLabel sx={{ mb: 1, display: 'block', fontWeight: 500 }}>Monto Pendiente</FormLabel>
              <TextField
                id="pendingAmount"
                name="pendingAmount"
                size="small"
                type="number"
                fullWidth
                value={formik.values.pendingAmount}
                onChange={formik.handleChange}
                error={formik.touched.pendingAmount && Boolean(formik.errors.pendingAmount)}
                helperText={formik.touched.pendingAmount && formik.errors.pendingAmount}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <AttachMoneyIcon sx={{ color: 'action.active', fontSize: 20 }} />
                    </InputAdornment>
                  )
                }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormLabel sx={{ mb: 1, display: 'block', fontWeight: 500 }}>Descuento</FormLabel>
              <TextField
                id="discount"
                name="discount"
                size="small"
                type="number"
                fullWidth
                placeholder={`En ${currency}`}
                value={formik.values.discount}
                onChange={(e) => {
                  let value = e.target.value.trim();
                  if (value === '') {
                    value = '';
                  } else {
                    value = value.replace(/\D/, '').slice(0, 6);
                    value = Math.max(0, Math.min(parseInt(value), formik.values.roomRent));
                    value = value === formik.values.roomRent ? formik.values.roomRent - 1 : value;
                  }
                  formik.setFieldValue('discount', value);
                }}
                error={formik.touched.discount && Boolean(formik.errors.discount)}
                helperText={formik.touched.discount && formik.errors.discount}
                disabled={formik.values.roomRent <= 0 || haveGST}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <DiscountIcon sx={{ color: 'action.active', fontSize: 20 }} />
                    </InputAdornment>
                  )
                }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormLabel sx={{ mb: 1, display: 'block', fontWeight: 500 }}>Método de Pago</FormLabel>
              <Select
                id="paymentMethod"
                name="paymentMethod"
                size="small"
                fullWidth
                value={formik.values.paymentMethod}
                onChange={formik.handleChange}
                error={formik.touched.paymentMethod && Boolean(formik.errors.paymentMethod)}
                startAdornment={
                  <InputAdornment position="start">
                    <PaymentIcon sx={{ color: 'action.active', fontSize: 20, ml: 1 }} />
                  </InputAdornment>
                }
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              >
                <MenuItem value="default" disabled>
                  Seleccionar Método de Pago
                </MenuItem>
                <MenuItem value="cash">Efectivo</MenuItem>
                <MenuItem value="creditCard">Tarjeta de Crédito</MenuItem>
                <MenuItem value="debitCard">Tarjeta de Débito</MenuItem>
                <MenuItem value="netBanking">Banca en Línea</MenuItem>
                <MenuItem value="onlinePayment">Pago en Línea</MenuItem>
              </Select>
            </Grid>

            {/* Impuestos */}
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2, mt: 2 }}>
                Impuestos
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Box
                sx={{
                  p: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  bgcolor: haveGST ? 'primary.lighter' : 'background.paper'
                }}
              >
                <FormControlLabel
                  control={<Switch checked={haveGST} onChange={handleGSTToggle} color="primary" />}
                  label={
                    <Typography variant="body2" fontWeight={500}>
                      ¿Tiene Impuesto?
                    </Typography>
                  }
                />
              </Box>
            </Grid>

            {haveGST && (
              <>
                <Grid item xs={12} sm={6}>
                  <FormLabel sx={{ mb: 1, display: 'block', fontWeight: 500 }}>Identidad Fiscal (Opcional) / RIF</FormLabel>
                  <TextField
                    id="gstNumber"
                    name="gstNumber"
                    size="small"
                    fullWidth
                    placeholder="Ej. J-12345678-9"
                    value={formik.values.gstNumber}
                    onChange={(e) => {
                      formik.handleChange(e);
                      calculateTotalAmount();
                    }}
                    error={formik.touched.gstNumber && Boolean(formik.errors.gstNumber)}
                    helperText={formik.touched.gstNumber && formik.errors.gstNumber}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>

                {!hotelData.roomgstpercentage && (
                  <Grid item xs={12} sm={6}>
                    <FormLabel sx={{ mb: 1, display: 'block', fontWeight: 500 }}>Porcentaje de Impuesto</FormLabel>
                    <TextField
                      id="gstPercentage"
                      name="gstPercentage"
                      size="small"
                      type="number"
                      fullWidth
                      placeholder="En %"
                      value={formik.values.gstPercentage}
                      onChange={(e) => {
                        formik.handleChange(e);
                        calculateTotalAmount();
                      }}
                      error={formik.touched.gstPercentage && Boolean(formik.errors.gstPercentage)}
                      helperText={formik.touched.gstPercentage && formik.errors.gstPercentage}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PercentIcon sx={{ color: 'action.active', fontSize: 20 }} />
                          </InputAdornment>
                        )
                      }}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                  </Grid>
                )}
              </>
            )}

            {/* Monto Total */}
            <Grid item xs={12} sm={6}>
              <FormLabel sx={{ mb: 1, display: 'block', fontWeight: 500 }}>Monto Total</FormLabel>
              <TextField
                id="totalAmount"
                name="totalAmount"
                size="small"
                type="number"
                fullWidth
                value={formik.values.totalAmount}
                onChange={formik.handleChange}
                error={formik.touched.totalAmount && Boolean(formik.errors.totalAmount)}
                helperText={formik.touched.totalAmount && formik.errors.totalAmount}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <AttachMoneyIcon sx={{ color: 'success.main', fontSize: 20 }} />
                    </InputAdornment>
                  )
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    bgcolor: 'success.lighter',
                    fontWeight: 600
                  }
                }}
              />
            </Grid>

            {/* Resumen */}
            <Grid item xs={12}>
              <Box
                sx={{
                  bgcolor: 'primary.lighter',
                  border: '1px solid',
                  borderColor: 'primary.light',
                  borderRadius: 2,
                  p: 2
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="body2" color="primary.main" fontWeight={500}>
                    💡 Resumen:
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total a Pagar (Incluye Descuentos e Impuestos): {currency} {formik.values.totalAmount || 0}
                    {haveGST && ` | (Base Imponible: ${currency} ${(formik.values.totalAmount - (gstAmount || 0)).toFixed(2)} + IVA: ${currency} ${gstAmount?.toFixed(2) || 0})`}
                  </Typography>
                </Stack>
              </Box>
            </Grid>
          </Grid>
        </form>
      </DialogContent>

      <Divider />

      {/* Actions */}
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button
          onClick={handleClose}
          variant="outlined"
          sx={{
            borderRadius: 2,
            px: 3,
            borderColor: 'divider',
            color: 'text.secondary',
            '&:hover': {
              borderColor: 'text.secondary',
              bgcolor: 'action.hover'
            }
          }}
        >
          Cancelar
        </Button>
        <Button
          onClick={formik.handleSubmit}
          variant="contained"
          color="primary"
          sx={{
            borderRadius: 2,
            px: 4,
            boxShadow: 2,
            '&:hover': {
              boxShadow: 4
            }
          }}
        >
          Generar Factura
        </Button>
      </DialogActions>
    </Dialog>
  );
};

RoomInvoiceDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  data: PropTypes.object.isRequired
};

export default RoomInvoiceDialog;
